import { useEffect, useState, useCallback, useRef } from 'react';
import { useDrive } from './useDrive';
import { defaultSyncEngine } from '../lib/sync-engine';
import { useNotesStore } from '../features/notes/notesStore';
import { useEventsStore } from '../features/calendar/eventsStore';
import { useActivityDetection, type ActivityLevel } from './useActivityDetection';
import { useToast } from '../contexts/ToastContext';

interface SyncState {
  isSyncing: boolean;
  lastSync: Date | null;
  error: string | null;
}

export const useSync = () => {
  const { isInitialized: driveReady } = useDrive();
  const { loadNotes, currentSpaceId } = useNotesStore();
  const { loadEvents } = useEventsStore();
  const { error: showErrorToast } = useToast();

  const [syncState, setSyncState] = useState<SyncState>({
    isSyncing: false,
    lastSync: null,
    error: null,
  });

  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPageVisibleRef = useRef(true);
  const hasShownAuthErrorRef = useRef(false);

  /**
   * Manual sync function
   */
  const sync = useCallback(async () => {
    console.log('🔄 [SYNC] Starting sync...');

    if (!driveReady) {
      console.warn('⚠️ [SYNC] Google Drive not ready, skipping sync');
      setSyncState((prev) => ({
        ...prev,
        error: 'Google Drive not initialized',
      }));
      return;
    }

    try {
      const syncStartTime = Date.now();
      setSyncState((prev) => ({ ...prev, isSyncing: true, error: null }));
      console.log('📤 [SYNC] Syncing notes and events to Drive...');

      // Sync both notes and events
      const [notesResult, eventsResult] = await Promise.all([
        defaultSyncEngine.syncAllNotes(),
        defaultSyncEngine.syncAllEvents(),
      ]);

      const syncDuration = Date.now() - syncStartTime;
      console.log(`✅ [SYNC] Sync completed in ${syncDuration}ms`);
      console.log(`   📝 Notes: ${notesResult.uploaded} uploaded, ${notesResult.downloaded} downloaded`);
      console.log(`   📅 Events: ${eventsResult.uploaded} uploaded, ${eventsResult.downloaded} downloaded`);

      const allSuccess = notesResult.success && eventsResult.success;
      const allErrors = [...notesResult.errors, ...eventsResult.errors];

      if (allErrors.length > 0) {
        console.error('❌ [SYNC] Sync errors:', allErrors);
      }

      // IMPORTANT: Always reload after sync to ensure UI reflects Drive state
      // This includes: new downloads from Drive, Drive deletion detection, etc.
      // We reload unconditionally because the sync engine modifies IndexedDB directly
      console.log('🔃 [SYNC] Reloading local data from IndexedDB...');
      await Promise.all([
        loadNotes(currentSpaceId),
        loadEvents(currentSpaceId),
      ]);

      if (allSuccess) {
        console.log('✅ [SYNC] Sync successful!');
        setSyncState({
          isSyncing: false,
          lastSync: new Date(),
          error: null,
        });
      } else {
        console.error('[useSync] Sync completed with errors:', allErrors);
        setSyncState({
          isSyncing: false,
          lastSync: new Date(),
          error: `Sync completed with errors: ${allErrors.join(', ')}`,
        });
      }
    } catch (error) {
      console.error('❌ [SYNC] Sync failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Sync failed';

      // Check if error is authentication-related
      const isAuthError = errorMessage.includes('401') ||
                         errorMessage.includes('unauthorized') ||
                         errorMessage.includes('authentication') ||
                         errorMessage.includes('token') ||
                         errorMessage.includes('credentials');

      // Show toast notification for auth errors (only once)
      if (isAuthError && !hasShownAuthErrorRef.current) {
        hasShownAuthErrorRef.current = true;
        showErrorToast('Session expired. Please sign out and log in again to continue syncing.', 8000);
      }

      setSyncState({
        isSyncing: false,
        lastSync: null,
        error: errorMessage,
      });
    }
  }, [driveReady, currentSpaceId, loadNotes, loadEvents, showErrorToast]);

  /**
   * Initial pull from Drive on first load
   */
  const pullFromDrive = useCallback(async () => {
    if (!driveReady) {
      console.warn('⚠️ [SYNC] Cannot pull from Drive - not initialized');
      return;
    }

    try {
      console.log('📥 [SYNC] Starting initial pull from Drive...');
      setSyncState((prev) => ({ ...prev, isSyncing: true, error: null }));

      // Pull both notes and events from Drive
      const [pulledNotes, pulledEvents] = await Promise.all([
        defaultSyncEngine.pullFromDrive(),
        defaultSyncEngine.pullEventsFromDrive(),
      ]);

      console.log(`📥 [SYNC] Pulled ${pulledNotes.length} notes and ${pulledEvents.length} events from Drive`);

      // Reload both notes and events after pull
      console.log('🔃 [SYNC] Reloading local data from IndexedDB...');
      await Promise.all([
        loadNotes(currentSpaceId),
        loadEvents(currentSpaceId),
      ]);

      console.log('✅ [SYNC] Initial pull complete');
      setSyncState({
        isSyncing: false,
        lastSync: new Date(),
        error: null,
      });
    } catch (error) {
      console.error('❌ [SYNC] Pull from Drive failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Pull failed';

      // Check if error is authentication-related
      const isAuthError = errorMessage.includes('401') ||
                         errorMessage.includes('unauthorized') ||
                         errorMessage.includes('authentication') ||
                         errorMessage.includes('token') ||
                         errorMessage.includes('credentials');

      // Show toast notification for auth errors (only once)
      if (isAuthError && !hasShownAuthErrorRef.current) {
        hasShownAuthErrorRef.current = true;
        showErrorToast('Session expired. Please sign out and log in again to continue syncing.', 8000);
      }

      setSyncState({
        isSyncing: false,
        lastSync: null,
        error: errorMessage,
      });
    }
  }, [driveReady, currentSpaceId, loadNotes, loadEvents, showErrorToast]);

  // Detect user activity for adaptive sync
  const activityLevel = useActivityDetection();

  /**
   * Get sync interval based on user activity level
   *
   * ALPHA TESTING MODE: Constant 30s sync interval
   * - Alpha testers will be on multiple devices watching for sync
   * - Long idle delays (15min) would cause confusion and false bug reports
   * - Constant interval makes sync behavior predictable and verifiable
   *
   * TODO: Re-enable adaptive sync for Beta release
   * Beta intervals (when re-enabled):
   * - Active: 30 seconds (user is working)
   * - Idle 1min: 3 minutes (user paused)
   * - Idle 5min: 15 minutes (user away)
   * - Idle long: Manual sync only
   */
  const getSyncInterval = useCallback((_activity: ActivityLevel): number => {
    // ALPHA TESTING: Constant 30s sync regardless of activity
    // This ensures predictable sync for multi-device testing
    return 30 * 1000; // 30 seconds

    /* BETA: Re-enable adaptive sync by uncommenting this code and renaming _activity to activity
    switch (activity) {
      case 'active':
        return 30 * 1000; // 30 seconds
      case 'idle-1min':
        return 3 * 60 * 1000; // 3 minutes
      case 'idle-5min':
        return 15 * 60 * 1000; // 15 minutes
      case 'idle-long':
        return 0; // Manual sync only
      default:
        return 60 * 1000; // Default 60 seconds (fallback)
    }
    */
  }, []);

  /**
   * Adaptive background sync based on user activity
   */
  useEffect(() => {
    if (!driveReady) {
      console.log('⏸️ [SYNC] Drive not ready, waiting...');
      return;
    }

    const interval = getSyncInterval(activityLevel);
    const intervalSeconds = Math.round(interval / 1000);

    // Clear existing interval
    if (syncIntervalRef.current) {
      console.log('🔄 [SYNC] Clearing previous sync interval');
      clearInterval(syncIntervalRef.current);
    }

    // Set new interval if not idle-long
    if (interval > 0) {
      console.log(`⏰ [SYNC] Starting adaptive sync:`);
      console.log(`   Activity level: ${activityLevel}`);
      console.log(`   Sync interval: ${intervalSeconds}s (${interval}ms)`);
      console.log(`   Next sync in ${intervalSeconds} seconds`);

      syncIntervalRef.current = setInterval(() => {
        if (isPageVisibleRef.current) {
          console.log(`⏰ [SYNC] Auto-sync triggered (activity: ${activityLevel}, interval: ${intervalSeconds}s)`);
          sync();
        } else {
          console.log(`⏸️ [SYNC] Page hidden, skipping auto-sync`);
        }
      }, interval);
    } else {
      console.log('⏸️ [SYNC] Auto-sync paused (user idle-long - manual sync only)');
    }

    // Cleanup
    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driveReady, activityLevel]); // Restart interval when driveReady or activity changes

  /**
   * Page Visibility API - pause sync when tab is hidden
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      isPageVisibleRef.current = !document.hidden;

      // Sync when page becomes visible
      if (!document.hidden && driveReady) {
        sync();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driveReady]); // Only re-attach listener when driveReady changes, not when sync changes

  /**
   * Initial sync on mount
   */
  useEffect(() => {
    if (driveReady) {
      console.log('🚀 [SYNC] Drive is ready, performing initial pull from Drive...');
      // Do an initial pull to get any existing notes and events from Drive
      pullFromDrive();
    } else {
      console.log('⏳ [SYNC] Waiting for Drive to be ready...');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driveReady]); // Only run when driveReady changes, not when pullFromDrive changes

  return {
    sync,
    pullFromDrive,
    isSyncing: syncState.isSyncing,
    lastSync: syncState.lastSync,
    error: syncState.error,
  };
};
